import React from 'react';
import styled from 'styled-components';
import * as FiIcons from 'react-icons/fi';
import { LandingShell, Container, PrimaryLink, GhostLink, Eyebrow, H2, Sub, T } from './ui';

const Ico = (n: keyof typeof FiIcons) => FiIcons[n] as React.ComponentType<any>;
const FiArrowRight = Ico('FiArrowRight');
const FiLayers = Ico('FiLayers');
const FiShield = Ico('FiShield');
const FiUserCheck = Ico('FiUserCheck');
const FiZap = Ico('FiZap');
const FiCheckCircle = Ico('FiCheckCircle');

const VALUES = [
  [FiLayers, 'One connected platform', 'Property, pest control, safety, and healthcare operations — unified, not stitched together.'],
  [FiShield, 'Secure by default', 'JWT auth, bcrypt passwords, per-role access, and HTTPS with auto-renewing certificates.'],
  [FiUserCheck, 'Access you control', 'People request access, your team reviews, and you approve exactly what each person needs.'],
  [FiZap, 'Ready in minutes', 'Containerized products you can preview today — no lengthy setup, no lock-in.'],
];

const AboutUsPage: React.FC = () => (
  <LandingShell>
    <Container>
      <Hero>
        <BlobA /><BlobB />
        <HeroInner>
          <Pill>Our story</Pill>
          <H1>Software for the operations that keep the world running.</H1>
          <Lead>
            ServiceBuds builds purpose-made products for the teams doing real work — managing
            properties, running pest-control routes, keeping workplaces safe, and caring for patients.
            One platform, one sign-in, no busywork.
          </Lead>
          <Actions>
            <PrimaryLink to="/signup">Request preview access <FiArrowRight /></PrimaryLink>
            <GhostLink to="/pay">Pay for services</GhostLink>
          </Actions>
        </HeroInner>
      </Hero>

      <Section>
        <Head><Eyebrow>Our mission</Eyebrow><H2>Give every team enterprise-grade tools</H2>
          <Sub>Too many operations run on spreadsheets and disconnected apps. We think the front office, the field, and the finances should finally speak the same language.</Sub></Head>
        <Split>
          <Story>
            <p>ServiceBuds started with a simple frustration: great operators were being held back by clunky, single-purpose software that never talked to each other.</p>
            <p>So we built a suite of products that share one design system, one login surface, and one standard for security — each still purpose-built for the exact work its users do every day.</p>
          </Story>
          <Bullets>
            {['Consistent experience across every product', 'Shared, controlled access — nobody sees more than they should', 'Compliance and security built in, not bolted on', 'Preview any product before you commit'].map(t => (
              <Bullet key={t}><FiCheckCircle /> {t}</Bullet>
            ))}
          </Bullets>
        </Split>
      </Section>

      <Section $muted>
        <Head><Eyebrow>What we value</Eyebrow><H2>The principles behind the platform</H2></Head>
        <Grid>
          {VALUES.map(([I, t, d]: any) => (
            <Card key={t}>
              <IconTile><I /></IconTile>
              <CardTitle>{t}</CardTitle>
              <CardText>{d}</CardText>
            </Card>
          ))}
        </Grid>
      </Section>

      <Cta>
        <CtaBlob />
        <CtaInner>
          <div>
            <CtaTitle>Ready to see ServiceBuds in action?</CtaTitle>
            <CtaSub>Request preview access, or pay for the services you need to get started.</CtaSub>
          </div>
          <CtaActions>
            <PrimaryLink to="/signup" style={{ background: '#fff', color: T.accentDark, boxShadow: '0 10px 24px rgba(0,0,0,.18)' }}>Request access <FiArrowRight /></PrimaryLink>
            <GhostLink to="/pay" style={{ color: '#fff', background: 'rgba(255,255,255,.10)', border: '1px solid rgba(255,255,255,.26)' }}>Pay for services</GhostLink>
          </CtaActions>
        </CtaInner>
      </Cta>
    </Container>
  </LandingShell>
);

export default AboutUsPage;

// ── styled ────────────────────────────────────────────────────────────────────
const Hero = styled.header`position:relative; overflow:hidden; margin-top:8px; border-radius:26px; background:${T.hero}; box-shadow:0 12px 30px rgba(14,63,58,.30);`;
const BlobA = styled.div`position:absolute; top:-90px; right:-50px; width:300px; height:300px; border-radius:50%; background:rgba(80,190,175,.18);`;
const BlobB = styled.div`position:absolute; bottom:-120px; left:20%; width:280px; height:280px; border-radius:50%; background:rgba(80,190,175,.10);`;
const HeroInner = styled.div`position:relative; z-index:1; padding:54px 40px 58px; max-width:720px;`;
const Pill = styled.span`display:inline-block; font-size:12px; font-weight:700; color:#CFEDE6; background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.20); border-radius:20px; padding:6px 12px; margin-bottom:18px;`;
const H1 = styled.h1`font-size:42px; line-height:1.07; font-weight:800; letter-spacing:-.025em; color:#fff; margin:0 0 16px; @media (max-width:640px){ font-size:32px; }`;
const Lead = styled.p`font-size:16.5px; line-height:1.6; color:rgba(214,240,235,.92); margin:0 0 26px;`;
const Actions = styled.div`display:flex; gap:12px; flex-wrap:wrap;`;

const Section = styled.section<{ $muted?: boolean }>`padding:56px 0; ${p => p.$muted && `background:linear-gradient(180deg,#EAEEEF,#E4EAEA); border-radius:24px; padding:56px 32px; margin-top:8px;`}`;
const Head = styled.div`max-width:680px; margin:0 auto 28px; text-align:center;`;
const Split = styled.div`display:grid; grid-template-columns:1.1fr .9fr; gap:36px; align-items:center; @media (max-width:800px){ grid-template-columns:1fr; }`;
const Story = styled.div`p{ font-size:15.5px; line-height:1.65; color:${T.text2}; margin:0 0 14px; }`;
const Bullets = styled.div`display:flex; flex-direction:column; gap:10px;`;
const Bullet = styled.div`display:flex; align-items:center; gap:10px; font-size:14px; font-weight:600; color:${T.text}; background:#fff; border:1px solid ${T.border}; border-radius:14px; padding:14px 16px; svg{ color:${T.accent}; font-size:18px; flex-shrink:0; }`;

const Grid = styled.div`display:grid; grid-template-columns:repeat(4,1fr); gap:18px; @media (max-width:900px){ grid-template-columns:repeat(2,1fr);} @media (max-width:520px){ grid-template-columns:1fr; }`;
const Card = styled.div`background:#fff; border:1px solid ${T.border}; border-radius:18px; padding:24px; box-shadow:0 4px 14px rgba(13,52,47,.05);`;
const IconTile = styled.div`width:46px; height:46px; border-radius:13px; display:flex; align-items:center; justify-content:center; font-size:22px; color:#fff; background:linear-gradient(135deg,${T.accent},${T.accentDark}); margin-bottom:14px; box-shadow:0 8px 16px rgba(14,63,58,.25);`;
const CardTitle = styled.h3`font-size:16px; font-weight:800; color:${T.text}; margin:0 0 6px;`;
const CardText = styled.p`font-size:13.5px; line-height:1.5; color:${T.text2}; margin:0;`;

const Cta = styled.div`position:relative; overflow:hidden; margin:36px 0 56px; border-radius:24px; background:${T.hero}; box-shadow:0 16px 40px rgba(14,63,58,.30);`;
const CtaBlob = styled.div`position:absolute; top:-90px; right:-40px; width:280px; height:280px; border-radius:50%; background:radial-gradient(circle,rgba(80,190,175,.25),transparent 70%);`;
const CtaInner = styled.div`position:relative; z-index:1; padding:34px 40px; display:flex; align-items:center; justify-content:space-between; gap:20px; flex-wrap:wrap;`;
const CtaTitle = styled.div`font-size:24px; font-weight:800; letter-spacing:-.02em; color:#fff;`;
const CtaSub = styled.div`font-size:14.5px; color:rgba(214,240,235,.9); margin-top:4px;`;
const CtaActions = styled.div`display:flex; gap:12px; flex-wrap:wrap;`;
